/**
 * 设计部「日期按美国时间填」的输入侧提示（2026-10-30 新增）。
 *
 * ── 需求背景 ────────────────────────────────────────────────────────────────
 * 用户原话：「注意处理现在设计部整个都是国外时间来显示，那么国内设置时间的时候
 * 要有提示让用户知道要设置国外时间才行哦」。
 *
 * 2026-10-29 已把设计部的**显示**统一到美国太平洋时间（`lib/usDate.js` +
 * 后端 `PROJECT_TZ`），但**输入侧**只在两个地方写了字（营销方案工单的「洛杉矶时间」
 * 列头 / 上门预约的「洛杉矶时间 · 不可超过首款到账后 5 个工作日」）。其余二十多处
 * 日期输入框是裸的 —— 中国同事（UTC+8，与洛杉矶差 15–16 小时）看到的日期是美国的，
 * 但输入框没告诉他「你填的这一格也是美国的」，很容易按北京日期填，
 * 结果差一天（选 10/24 实际落成 10/23）。
 *
 * ── 这一层的语义是什么 ──────────────────────────────────────────────────────
 * 后端 `design_module.tz_day_start()` 的口径（2026-10-29 定稿）：用户在输入框里
 * 选的 `YYYY-MM-DD` **一律按洛杉矶日历日的 00:00** 落库，不看提交者时区。
 * 所以提示要说的不是「换算」，而是「直接填美国日历上的那一天」——
 * 中国同事在北京时间 10/30 上午 9 点（洛杉矶还是 10/29）想表示"明天"，
 * 该填 10/30（美国日历的明天），而不是按北京时间算出来的某个偏移值。
 *
 * ── 为什么统一成一个组件，而不是每个输入框手写一行小字 ────────────────────
 * ① 二十多处各写一套文案，必然出现口径漂移（已有先例：营销方案标「中国时间」、
 *    其它页面按浏览器本地，2026-10-29 就是被这种漂移拖了整轮）；
 * ② 提示的长短要随版面收放（抽屉里一行字、弹窗里两行），集中一处才好调；
 * ③ 文案进 i18n 字典时只需要一个 key，翻译不会出现七八种说法。
 *
 * ── 放在哪、怎么用 ────────────────────────────────────────────────────────
 * 紧贴输入框下方（表单纵向布局）或右侧（工具条横向布局），用 `<UsDateHint />`
 * 默认形态；需要更短时传 `compact`，只显示「美国时间」四个字 + title 全文。
 * 只用于**设计部**的日期/时刻输入；HRMS、财务、行政审批等中国主体业务
 * 仍按各自时区运营，不要往那边挂这个提示。
 */
import React from "react";
import { Globe } from "lucide-react";
import { T } from "../../../lib/i18n";

/** 完整提示文案（挂在 title 与正文里，两处用同一份，避免悬停和正文说两套话）。
 *  写成「日期 / 时刻」是因为设计部既有一批 `type="date"`（只选日），也有
 *  `type="datetime-local"`（上门预约、修改承诺时间要选到点），一句话覆盖两种控件。 */
export const US_DATE_TIP = "按美国时间（洛杉矶）填：填的日期 / 时刻就是美国当地的日期 / 时刻，中美同事看到的完全一致，不用换算成北京时间。";

/**
 * 日期输入框旁的一行小字提示。
 *
 * @param {boolean} compact 紧凑形态：只显示「美国时间」标签，全文收进 title。
 *                          工具条 / 表格行内这类横向空间紧张的位置用它。
 * @param {string}  testid  可选的 data-testid，供 E2E 断言提示是否在位。
 * @param {string}  className 追加样式（例如需要贴右对齐时传 ml-auto）。
 * @returns {JSX.Element}
 */
export const UsDateHint = ({ compact = false, testid, className = "" }) => {
  const tip = T(US_DATE_TIP);
  if (compact) {
    return (
      <span title={tip} data-testid={testid}
        className={`inline-flex items-center gap-0.5 rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[9px] font-medium leading-none text-slate-500 ${className}`}>
        <Globe className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
        {T("美国时间")}
      </span>
    );
  }
  return (
    <span title={tip} data-testid={testid}
      className={`mt-1 flex items-start gap-1 text-[10px] font-normal leading-relaxed text-slate-400 ${className}`}>
      <Globe className="mt-[1px] h-3 w-3 shrink-0" aria-hidden="true" />
      <span>{tip}</span>
    </span>
  );
};

export default UsDateHint;
